/* eslint-disable react/jsx-props-no-spreading */
import { CloseIcon } from '@coop/icons';
import { splitCustomObjectFromIntersection } from '@coop/utils';
import { Slot, Slottable } from '@radix-ui/react-slot';
import classNames from 'classnames';

import { Icon } from '../Icon';
import styles from './Tag.module.scss';
import type {
    TagBaseProps,
    TagProps,
    TagReadOnly,
    TagReadOnlyBase,
    TagSelectable,
    TagSelectableBase,
} from './Tag.types';

const Tag = (props: TagProps) => {
    const type =
        (props.icon && 'WithLeftSlot') || (props.rightSlot && 'WithRightSlot') || 'WithTextOnly';

    const classes = classNames(
        props.className,
        styles.Tag,
        props.type === 'selectable' && styles.Selectable,
        props.type === 'read-only' && styles.ReadOnly,
        styles[`Theme--${props.theme}`],
        styles[type],
    );

    if (props.type === 'selectable') {
        const { custom: customProps, rest: buttonProps } = splitCustomObjectFromIntersection<
            TagBaseProps & TagSelectable,
            TagSelectableBase
        >(props, {
            type: props.type,
            theme: props.theme,
            icon: props.icon,
            asChild: props.asChild,
            className: props.className,
            rightSlot: props.rightSlot,
            number: props.number,
            children: props.children,
            isActive: props.isActive,
        });

        const useHtmlButtonElement = !customProps.asChild;

        const Component = useHtmlButtonElement ? 'button' : Slot;

        return (
            <Component
                className={classNames(classes, customProps.isActive && styles.IsActive)}
                type="button"
                {...buttonProps}
            >
                {customProps.icon && (
                    <div className={styles.LeftIcon} aria-hidden>
                        <Icon icon={customProps.icon} />
                    </div>
                )}
                <Slottable>{customProps.children}</Slottable>
                {customProps.number != null && <span>({customProps.number})</span>}
                {customProps.isActive && (
                    <div className={styles.CloseIcon} aria-hidden>
                        <Icon icon={CloseIcon} />
                    </div>
                )}
            </Component>
        );
    }

    if (props.type === 'read-only') {
        const { custom: customProps, rest: divProps } = splitCustomObjectFromIntersection<
            TagBaseProps & TagReadOnly,
            TagReadOnlyBase
        >(props, {
            type: props.type,
            theme: props.theme,
            icon: props.icon,
            asChild: props.asChild,
            className: props.className,
            rightSlot: props.rightSlot,
            number: props.number,
            children: props.children,
        });

        return (
            <div className={classes} {...divProps}>
                {customProps.icon && (
                    <div className={styles.LeftIcon} aria-hidden>
                        <Icon icon={customProps.icon} />
                    </div>
                )}
                <Slottable>{customProps.children}</Slottable>
                {props.number != null && <span>({props.number})</span>}
            </div>
        );
    }

    return exhaustiveCheck(props);
};

const exhaustiveCheck = (value: never): never => {
    throw new Error(`Unhandled value: ${value}`);
};

export default Tag;
